import React from 'react';
import { Box } from '@chakra-ui/react';
import { motion } from 'framer-motion';

/**
 * Personagem isométrico minimalista (estilo "Habbo minimizado") — SVG leve,
 * sem WebGL. Pensado para renderizar dezenas numa cena.
 *  • corpo por gênero (F/M/neutro);
 *  • camiseta na cor da função (role);
 *  • sombra isométrica (elipse) na base;
 *  • bob de "idle" (respira) e modo "walking" (passo mais rápido);
 *  • vira de lado via flip horizontal quando anda p/ a esquerda.
 * O posicionamento na grade fica a cargo do palco (IsoStage), não daqui.
 */

export type IsoGender = 'F' | 'M' | 'N';

export interface IsoCharacterProps {
  gender: IsoGender;
  /** Cor da função (camiseta). */
  color: string;
  /** Tom de pele. */
  skin?: string;
  /** Está caminhando? (passo mais rápido) */
  walking?: boolean;
  /** Direção horizontal do passo (-1 esquerda, 1 direita). */
  facing?: -1 | 1;
  /** Tamanho em px (altura aproximada do boneco). */
  size?: number;
  /** Atraso da animação (pra dessincronizar a cena). */
  delay?: number;
}

const SKINS = ['#F1C27D', '#E0AC69', '#C68642', '#8D5524'];
export const skinFor = (seed: number) => SKINS[Math.abs(seed) % SKINS.length];

const HAIR: Record<IsoGender, string> = { F: '#3B2B26', M: '#2A2320', N: '#4A3B33' };

export function IsoCharacter({
  gender,
  color,
  skin = '#E0AC69',
  walking = false,
  facing = 1,
  size = 72,
  delay = 0,
}: IsoCharacterProps) {
  const hair = HAIR[gender];
  const w = size * 0.62;

  return (
    <Box
      position="relative"
      w={`${w}px`}
      h={`${size}px`}
      style={{ transform: `scaleX(${facing})` }}
    >
      {/* sombra isométrica */}
      <Box
        position="absolute"
        bottom="-2px"
        left="50%"
        w={`${w * 0.74}px`}
        h={`${w * 0.32}px`}
        style={{
          transform: 'translateX(-50%)',
          background: 'radial-gradient(ellipse, rgba(7,18,40,0.32), transparent 70%)',
        }}
      />
      <motion.div
        animate={
          walking
            ? { y: [0, -2, 0], rotate: [0, 1.2, 0, -1.2, 0] }
            : { scaleY: [1, 1.022, 1], scaleX: [1, 0.992, 1] }
        }
        transition={{ duration: walking ? 0.5 : 3.6, repeat: Infinity, ease: 'easeInOut', delay }}
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          transformOrigin: 'bottom center',
        }}
      >
        <svg
          viewBox="0 0 62 96"
          width={w}
          height={size}
          style={{ display: 'block', overflow: 'visible' }}
        >
          {/* pernas */}
          <rect x="24" y="66" width="6" height="20" rx="3" fill="#3A4658" />
          <rect x="32" y="66" width="6" height="20" rx="3" fill="#2E3949" />
          {/* sapatos */}
          <ellipse cx="27" cy="88" rx="5" ry="3" fill="#1F2733" />
          <ellipse cx="35" cy="88" rx="5" ry="3" fill="#1F2733" />
          {/* tronco / camiseta da função */}
          <path
            d="M20 50c0-7 5-11 11-11s11 4 11 11v18c0 3-2 5-5 5H25c-3 0-5-2-5-5V50z"
            fill={color}
          />
          {/* sombreado lateral da camiseta (volume) */}
          <path d="M31 39c6 0 11 4 11 11v18c0 3-2 5-5 5h-6V39z" fill="rgba(0,0,0,0.10)" />
          {/* braços */}
          <rect x="15" y="50" width="6" height="18" rx="3" fill={color} />
          <rect x="41" y="50" width="6" height="18" rx="3" fill={color} />
          {/* mãos */}
          <circle cx="18" cy="69" r="3.4" fill={skin} />
          <circle cx="44" cy="69" r="3.4" fill={skin} />
          {/* pescoço */}
          <rect x="28" y="32" width="6" height="8" rx="3" fill={skin} />
          {/* cabeça */}
          <circle cx="31" cy="24" r="13" fill={skin} />
          {/* cabelo por gênero */}
          {gender === 'F' ? (
            <path
              d="M18 24c0-9 6-15 13-15s13 6 13 15c0 3-1 6-2 8l-3-2c1-7 0-13-8-13s-9 6-8 13l-3 2c-1-2-2-5-2-8z"
              fill={hair}
            />
          ) : (
            <path d="M19 22c1-8 6-13 12-13s11 5 12 13c-3-4-7-6-12-6s-9 2-12 6z" fill={hair} />
          )}
          {/* rosto */}
          <circle cx="27" cy="24" r="1.5" fill="#2B2B2B" />
          <circle cx="35" cy="24" r="1.5" fill="#2B2B2B" />
          <path
            d="M28 29c1.6 1.4 4.4 1.4 6 0"
            stroke="#2B2B2B"
            strokeWidth="1.3"
            fill="none"
            strokeLinecap="round"
          />
          {/* brilho especular na cabeça */}
          <ellipse cx="26" cy="18" rx="4" ry="3" fill="rgba(255,255,255,0.28)" />
        </svg>
      </motion.div>
    </Box>
  );
}
